import { isEqual as _isEqual } from 'lodash';
import { applyMiddleware, combineReducers, createStore } from 'redux';
import { multiClientMiddleware } from 'redux-axios-middleware';
import { composeWithDevTools } from 'redux-devtools-extension';
import { persistStore } from 'redux-persist';
import thunk from 'redux-thunk';

import {
  AUTH_API_CLIENT_NAME,
  COMPEON_API_CLIENT_NAME,
  COMPEON_OAUTH_API_CLIENT_NAME,
  WHITE_LABEL_API_CLIENT_NAME,
  MOCK_API_CLIENT_NAME,
  CompeonReverseClientInstance,
  CompeonClientInstance,
  AuthClientInstance,
  CompeonOAuthClientInstance,
  MockApiClientInstance,
} from 'api';
import {
  languageReducer,
  type LanguageState,
} from 'components/App/LanguageProvider/store/reducers';
import { ICompanyDetailsState } from 'models/CompanyDetails.model';
import companyDetails from 'pages/operationPortal/CompaniesDetails/store/reducer';
import companiesList, { ICompaniesList } from 'pages/operationPortal/CompaniesList/store/reducer';
import filterManagementReducer, {
  IMarketDataFilterState,
} from 'pages/operationPortal/PlanningEvaluation/SharedComponent/filterManagementReducer';
import banks from 'store/banks/reducer';
import config from 'store/config/reducer';
import { documentExchangeReducer } from 'store/documentExchange/documentExchange.reducer';
import emailAvailable from 'store/emailAvailable/reducer';
import inquiries from 'store/inquiries/reducer';
import { IInquiriesListState, inquiryListReducer } from 'store/inquiryList/reducer';
import inquiryProcess from 'store/inquiryProcess/reducer';
import { layoutReducer } from 'store/layout/reducer';
import partner from 'store/partner/reducer';
import progress from 'store/progress/reducer';
import user from 'store/user/reducer';
import { UserAttributes } from 'types/User';
import { isDevelopment } from 'utils/environment';
import { getUserAttributes } from 'utils/user/getters';

import { IInquiryDetailsState, inquiryDetailsReducer } from './inquiryDetails/reducer';
import { kpiReducer as kpisList } from './kpi/kpis.reducer';
import { newInquiryDetailsReducer } from './newInquiryDetails/reducer';
import {
  IProfitabilityEvaluationState,
  profitabilityEvaluationReducer,
} from '../mittweida/portals/operations/store/reducer';
import { trackUserId } from '../utils/tracker/actions/trackUserId';

const applyCommonMiddleware = applyMiddleware(
  thunk,
  multiClientMiddleware({
    [WHITE_LABEL_API_CLIENT_NAME]: {
      client: CompeonReverseClientInstance,
    },
    [COMPEON_API_CLIENT_NAME]: {
      client: CompeonClientInstance,
    },
    [AUTH_API_CLIENT_NAME]: {
      client: AuthClientInstance,
    },
    [COMPEON_OAUTH_API_CLIENT_NAME]: {
      client: CompeonOAuthClientInstance,
    },
    [MOCK_API_CLIENT_NAME]: {
      client: MockApiClientInstance,
    },
    default: {
      client: CompeonReverseClientInstance,
    },
  }),
);

const middleware = isDevelopment()
  ? composeWithDevTools(applyCommonMiddleware)
  : applyCommonMiddleware;

const store = createStore(
  combineReducers({
    partner,
    user,
    progress,
    banks,
    emailAvailable,
    inquiries,
    inquiryProcess,
    config,
    companiesList,
    kpisList,
    companyDetails,
    layout: layoutReducer,
    documentExchange: documentExchangeReducer,
    inquiryDetails: inquiryDetailsReducer,
    newInquiryDetails: newInquiryDetailsReducer,
    inquiriesList: inquiryListReducer,
    marketDataFilter: filterManagementReducer,
    profitability: profitabilityEvaluationReducer,
    language: languageReducer,
  }),
  middleware,
);

const persistor = persistStore(store, undefined, () => {
  const userAttributes = getUserAttributes();
  const userId = _isEqual(userAttributes, {}) ? undefined : (userAttributes as UserAttributes).id;
  if (userId) {
    trackUserId({ id: userId });
  }
});

if ('Cypress' in window) {
  const w = window as any;
  w.store = store;
}
export interface IState {
  partner: any;
  user: any;
  progress: any;
  banks: any;
  emailAvailable: any;
  inquiries: any;
  inquiryProcess: any;
  config: any;
  layout: any;
  documentExchange: any;
  companiesList: ICompaniesList;
  companyDetails: ICompanyDetailsState;
  inquiryDetails: IInquiryDetailsState;
  inquiriesList: IInquiriesListState;
  marketDataFilter: IMarketDataFilterState;
  profitability: IProfitabilityEvaluationState;
  language: LanguageState;
}

export { store, persistor };

export type AppDispatch = typeof store.dispatch;
